import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  setFibiSpeaking: (value: boolean) => void;
  setFibiProcessing: (value: boolean) => void;
  startListening: () => void;
  enabled: boolean;
  isPaused?: boolean;
  setIsPaused?: (value: boolean) => void;
}

const useFibiAudioContext = ({
  setFibiProcessing,
  setFibiSpeaking,
  startListening,
  enabled,
  isPaused = false,
  setIsPaused,
}: Props) => {
  // AudioContext and scheduling
  // Only initialize refs when enabled
  const audioContextRef = useRef<AudioContext | null>(null);
  const isContextInitialized = useRef(false);
  const initialBufferTime = 0.05; // Increased from 0.05 to 100ms
  const nextStartRef = useRef<number>(0);
  const INITIAL_DELAY = 0.5; // 5 is 5seconds

  const isFirstChunkRef = useRef(true);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const lastScheduledEndTimeRef = useRef(0);
  const playbackCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bufferedDataRef = useRef<Uint8Array | null>(
    enabled ? new Uint8Array() : null
  );
  const lastChunkReceivedAtRef = useRef<number | null>(null);
  const chunkArrivalTimesRef = useRef<number[]>([]);
  const totalChunksReceivedRef = useRef(0);
  const lastStatusUpdateRef = useRef<number>(Date.now());
  const networkErrorTimeoutRef = useRef<NodeJS.Timeout>();

  // Pause/Resume functionality
  const pausedChunksQueue = useRef<{ chunk: Uint8Array; done: boolean }[]>([]);
  const wasPlayingBeforePause = useRef<boolean>(false);

  // Only initialize state when enabled
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [networkStatus, setNetworkStatus] = useState<
    "stable" | "unstable" | "disconnected"
  >("stable");

  // Initialize or resume AudioContext
  const ensureAudioContext = useCallback(async () => {
    if (!enabled) return;
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)({ sampleRate: 24000 });
      await audioContextRef.current.resume();
      isContextInitialized.current = true;

      // Prime the timing system with a small future start time
      nextStartRef.current =
        audioContextRef.current.currentTime + initialBufferTime;
    }
  }, [enabled]);

  // Add a reset function
  const resetAudioState = useCallback(() => {
    if (!enabled) return;

    // Clear any existing audio data
    if (bufferedDataRef.current) {
      bufferedDataRef.current = new Uint8Array();
    } else {
      bufferedDataRef.current = new Uint8Array();
    }
    isFirstChunkRef.current = true;

    // Clear any pending completion timeout
    if (playbackCompleteTimeoutRef.current) {
      clearTimeout(playbackCompleteTimeoutRef.current);
      playbackCompleteTimeoutRef.current = null;
    }

    // Clear network timeout
    if (networkErrorTimeoutRef.current) {
      clearTimeout(networkErrorTimeoutRef.current);
      networkErrorTimeoutRef.current = undefined;
    }

    // Reset timing references
    nextStartRef.current = 0;
    lastScheduledEndTimeRef.current = 0;
    if (lastStatusUpdateRef.current !== null) {
      lastStatusUpdateRef.current = Date.now();
    }

    // Reset states
    setFibiSpeaking(false);
    setNetworkStatus("stable");
    setIsNetworkError(false);
  }, [setFibiSpeaking, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    if (!enabled) return;

    return () => {
      // Stop all active sources
      activeSourcesRef.current.forEach((source) => {
        try {
          source.stop();
        } catch (e) {
          console.warn("Failed to stop source:", e);
        }
      });
      activeSourcesRef.current = [];

      // Suspend or close the AudioContext
      if (audioContextRef.current) {
        if (audioContextRef.current.state === "running") {
          audioContextRef.current.suspend().catch((e) => {
            console.warn("Failed to suspend AudioContext:", e);
          });
        }
      }

      // Clear timeouts
      if (playbackCompleteTimeoutRef.current) {
        clearTimeout(playbackCompleteTimeoutRef.current);
        playbackCompleteTimeoutRef.current = null;
      }
      if (networkErrorTimeoutRef.current) {
        clearTimeout(networkErrorTimeoutRef.current);
        networkErrorTimeoutRef.current = undefined;
      }

      // Reset state and references
      bufferedDataRef.current = null;
      isContextInitialized.current = false;
      isFirstChunkRef.current = true;
      nextStartRef.current = 0;
      lastScheduledEndTimeRef.current = 0;
    };
  }, [enabled]);

  const schedulePlaybackComplete = useCallback(() => {
    if (!enabled) return;
    const ctx = audioContextRef.current;
    if (!ctx) return;

    // Clear any existing timeout
    if (playbackCompleteTimeoutRef.current) {
      clearTimeout(playbackCompleteTimeoutRef.current);
    }

    // Calculate how many milliseconds until the last audio finishes playing
    const now = ctx.currentTime;
    const timeUntilComplete = (lastScheduledEndTimeRef.current - now) * 1000;

    // Schedule the completion event after all audio has played
    // Add a small buffer (50ms) to ensure it's really done
    const delayMs = Math.max(timeUntilComplete + 50, 0);

    playbackCompleteTimeoutRef.current = setTimeout(() => {
      // console.log("Audio playback complete");
      setFibiProcessing(false);
      setFibiSpeaking(false);
      startListening();
      isFirstChunkRef.current = true; // Reset for next stream
      playbackCompleteTimeoutRef.current = null;
    }, delayMs);
  }, [setFibiProcessing, setFibiSpeaking, startListening, enabled]);

  const processChunkInternal = useCallback(
    async (chunk: Uint8Array, done = false) => {
      if (!enabled) return;
      const ctx = audioContextRef.current;
      if (!ctx) return;

      // First chunk detection
      if (isFirstChunkRef.current && chunk.length > 0) {
        // console.log("🎵 Audio playback starting");
        setFibiSpeaking(true);
        isFirstChunkRef.current = false;

        nextStartRef.current = ctx.currentTime + INITIAL_DELAY;

        // Clear any existing completion timeout
        if (playbackCompleteTimeoutRef.current) {
          clearTimeout(playbackCompleteTimeoutRef.current);
          playbackCompleteTimeoutRef.current = null;
        }
      }

      // Merge carry-over
      if (!bufferedDataRef.current) {
        bufferedDataRef.current = new Uint8Array();
      }

      const combined = new Uint8Array(
        bufferedDataRef.current.length + chunk.length
      );
      combined.set(bufferedDataRef.current);
      combined.set(chunk, bufferedDataRef.current.length);

      // Ensure even length
      const validLength = combined.length - (combined.length % 2);
      const validData = combined.subarray(0, validLength);
      bufferedDataRef.current = combined.subarray(validLength);

      const sampleCount = validData.length / 2;
      if (sampleCount <= 0) {
        if (done) {
          schedulePlaybackComplete();
        }
        return;
      }

      // Build AudioBuffer
      let buffer;
      try {
        buffer = ctx.createBuffer(1, sampleCount, 24000);
      } catch (e) {
        console.error("❌ Failed to create AudioBuffer:", e);
        return;
      }

      const channel = buffer.getChannelData(0);
      const view = new DataView(validData.buffer);

      let errors = 0;
      for (let i = 0; i < sampleCount; i++) {
        try {
          channel[i] = view.getInt16(i * 2, true) / 32768;
        } catch {
          channel[i] = 0;
          errors++;
        }
      }
      if (errors) {
        console.warn(`⚠️ Corrupted samples: ${errors}/${sampleCount}`);
      }

      // Scheduling playback
      const now = ctx.currentTime;
      let startTime = Math.max(nextStartRef.current, now + 0.05);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);

      // Track active source
      activeSourcesRef.current.push(source);

      source.onended = () => {
        activeSourcesRef.current = activeSourcesRef.current.filter(
          (s) => s !== source
        );
      };

      source.start(startTime);

      const endTime = startTime + buffer.duration;
      nextStartRef.current = endTime;
      lastScheduledEndTimeRef.current = Math.max(
        lastScheduledEndTimeRef.current,
        endTime
      );

      // Buffer health check
      const remainingBufferedTime =
        lastScheduledEndTimeRef.current - ctx.currentTime;
      const SAFETY_MARGIN = 0.5; // seconds
      const RESUME_THRESHOLD = 2.0; // seconds

      if (remainingBufferedTime < SAFETY_MARGIN && ctx.state === "running") {
        console.warn("🚨 Buffer underrun detected. Pausing AudioContext.");
        setIsNetworkError(true);
        setNetworkStatus("unstable");
        try {
          await ctx.suspend();
          // setIsAudioPaused(true);
        } catch (e) {
          console.error("❌ Failed to suspend AudioContext:", e);
        }
      } else if (
        remainingBufferedTime >= RESUME_THRESHOLD &&
        ctx.state === "suspended"
      ) {
        // console.log("✅ Buffer rebuilt. Resuming AudioContext.");
        try {
          await ctx.resume();
          setNetworkStatus("stable");
          setIsNetworkError(false);
        } catch (e) {
          console.error("❌ Failed to resume AudioContext:", e);
        }
      }

      // ✨ [NEW EDGE CASE HANDLING] ✨
      if (done) {
        if (ctx.state === "suspended") {
          // console.log("🚀 Final chunks arrived. Forcing AudioContext resume.");
          try {
            await ctx.resume();
            // setIsAudioPaused(false);
          } catch (e) {
            console.error("❌ Failed to resume AudioContext at end:", e);
          }
        }
        schedulePlaybackComplete();
      }
    },
    [schedulePlaybackComplete, setFibiSpeaking, enabled]
  );

  // Pause audio playback
  const pauseAudio = useCallback(async () => {
    if (!enabled || !audioContextRef.current) return;

    wasPlayingBeforePause.current = audioContextRef.current.state === "running";

    if (audioContextRef.current.state === "running") {
      try {
        await audioContextRef.current.suspend();
      } catch (e) {
        console.error("❌ Failed to pause AudioContext:", e);
      }
    }

    if (setIsPaused) {
      setIsPaused(true);
    }
  }, [enabled, setIsPaused]);

  // Resume audio playback
  const resumeAudio = useCallback(async () => {
    if (!enabled || !audioContextRef.current) return;

    if (
      audioContextRef.current.state === "suspended" &&
      wasPlayingBeforePause.current
    ) {
      try {
        await audioContextRef.current.resume();
      } catch (e) {
        console.error("❌ Failed to resume AudioContext:", e);
      }
    }

    // Process any queued chunks
    if (pausedChunksQueue.current.length > 0) {
      for (const queuedItem of pausedChunksQueue.current) {
        await processChunkInternal(queuedItem.chunk, queuedItem.done);
      }

      pausedChunksQueue.current = [];
    }

    if (setIsPaused) {
      setIsPaused(false);
    }
  }, [enabled, setIsPaused, processChunkInternal]);

  // Wrapper processChunk that handles pause/resume
  const processChunk = useCallback(
    async (chunk: Uint8Array, done = false) => {
      if (!enabled) return;

      // If paused, queue the chunk
      if (isPaused) {
        pausedChunksQueue.current.push({ chunk, done });
        return;
      }

      // Process normally if not paused
      await processChunkInternal(chunk, done);
    },
    [enabled, isPaused, processChunkInternal]
  );

  return {
    isNetworkError,
    networkStatus,
    setNetworkStatus,
    setIsNetworkError,
    lastStatusUpdateRef,
    lastChunkReceivedAtRef,
    ensureAudioContext,
    processChunk,
    chunkArrivalTimesRef,
    networkErrorTimeoutRef,
    totalChunksReceivedRef,
    audioContextRef,
    resetAudioState,
    pauseAudio,
    resumeAudio,
  };
};

export default useFibiAudioContext;
